
import { logEvent, setAnalyticsUserId, clearAnalyticsUserId } from './analytics';
import { getSendFeeCategorySubtext } from './monthly_gift_pricing.js';
const config = require('./config');
const authServices = require('./authServices');
const gg_fees = require('./gg_pricing');
let baseUrl = config.gg_api_baseurl;

// check if a username, email, or phone number is unique
function uniqueLookup(url) {
    return fetch(url)
        .then(async (response) => {
            const json = await response.json().catch(() => ({}));
            if (!response.ok || typeof json.unique !== 'boolean') {
                return { status: 'error' };
            }
            return { status: 'ok', unique: json.unique };
        })
        .catch(() => ({ status: 'error' }));
}

// Persist entitlements 
function storeEntitlements(entitlements) {
    localStorage.setItem(
        'entitlements',
        JSON.stringify(entitlements != null ? entitlements : null)
    );
}

const GGApi = {
    
    /* -----------------  login interactions  --------------------------------- */

    // attempt to log in and authenticate a user
    log_in(login_attempt) {
        try {
            console.log('trying to authenticate user');
            return fetch(`${baseUrl}/users/login` , {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(login_attempt)
            
            }).then(response => {
                return response.json();
            }).then(jsonResponse => {
                
                return jsonResponse;
            });
        } catch(e) {
            console.log(e);
        };
    },

    // check if a token is valid
    // if valid - return true, and save a new token to the local storage
    // if not valid - return false, and remove the existing token from the local storage
    checkToken() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/users/validateSession`, {
            headers: headers
        })
        .then(response => {
            // dont sign out the user if the server is down
            if (response.status >= 500) {
                return { serverError: true };
            }
            return response.json().catch(() => ({ serverError: true }));
        }).then (jsonResponse => {

            if (jsonResponse.serverError) {
                // Keep the existing session; the next call can try again.
                return !!localStorage.getItem('user');
            }

            if (jsonResponse.newToken) {
                // token was valid, and a new token was sent as a refresh, following user activity on the app
                let userId = jsonResponse.newToken.userId;
                if (userId == null) {
                    userId = authServices.currentUserId();
                }
                if (userId == null) {
                    userId = authServices.userIdFromToken(jsonResponse.newToken.accessToken);
                }
                const refreshedUser = userId != null
                    ? Object.assign({}, jsonResponse.newToken, { userId: userId })
                    : jsonResponse.newToken;

                localStorage.setItem("user", JSON.stringify(refreshedUser));
                storeEntitlements(jsonResponse.entitlements);
                setAnalyticsUserId(userId);

            } else if (!jsonResponse.tokenIsValid) {
                // Session expired — clear local auth and analytics identity
                const hadUser = !!localStorage.getItem('user');
                if (hadUser) {
                    logEvent('auth: logout', { source: 'session_timeout' });
                }
                clearAnalyticsUserId();
                localStorage.removeItem("user");
                localStorage.removeItem("entitlements");

            };
            return jsonResponse.tokenIsValid;
        })
        .catch(() => {
            // Network failure (offline, DNS, CORS). Not an auth decision.
            return !!localStorage.getItem('user');
        })
        
    },

    /* -----------------  2FA  --------------------------------- */

    // check if a token is valid
    // if valid - return true, and save a new token to the local storage
    // if not valid - return false, and remove the existing token from the local storage
    check2FAToken() {
        const data_for_2fa = JSON.parse(localStorage.getItem('data_for_2fa'));
        let token = data_for_2fa.token_2fa
        let headers = 
            {
            'x-access-token': token, 
            };
        return fetch(`${baseUrl}/users/validate2FA`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then (jsonResponse => {
            
            if (!jsonResponse.tokenIsValid) {
                // the token is not valid. remove it from the local storage, and redirect to login page
                localStorage.removeItem("data_for_2fa");
                window.location.href = 'login';
            };
            return jsonResponse.tokenIsValid;
        })
        
    },

    // send_otp_for_2fa
    send_otp_for_2fa(verification_method) {
        const data_for_2fa = JSON.parse(localStorage.getItem('data_for_2fa'));
        let token = data_for_2fa.token_2fa
        let headers = 
            {
            "Content-Type": "application/json",
            'x-access-token': token
            };
        
        return fetch(`${baseUrl}/users/validate2FA/send_otp` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                { verification_method: verification_method })
        }).then(response => {
            return response.json();
        })
    },

    // check the otp input
    check_otp_for_2fa(otp, remember_me){
        const data_for_2fa = JSON.parse(localStorage.getItem('data_for_2fa'));
        let token = data_for_2fa.token_2fa
        let headers = 
            {
            "Content-Type": "application/json",
            'x-access-token': token
            };
        
        return fetch(`${baseUrl}/users/validate2FA/check_otp` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {  otp: otp, remember_me: remember_me })
        }).then(response => {
            return response.json();
        }).then(jsonResponse => {
            // Wrong OTP returns { message } with no token — guard before accessing
            if(jsonResponse.token?.accessToken){
                localStorage.setItem("user", JSON.stringify(jsonResponse.token));
                storeEntitlements(jsonResponse.entitlements);
                setAnalyticsUserId(jsonResponse.token.userId);
                logEvent('login: successful 2fa login', { source: 'login_verify' });
            };
            return jsonResponse;
        })
    },

    /* -----------------  GG user creation & interactions --------------------------------- */
    
    // Get verification code
    verify_phone_number(number) {

        return fetch(`${baseUrl}/users/verify/create_phone` , {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({number: number})

        }).then(response => {
            return response.json();
        });
    },

    // verify verification code
    verify_check_number(number, password_input) {
        /*  verification = {
                phone_number: number,
                password: password_input
            } */
        try {
            return fetch(`${baseUrl}/users/verify/check_phone` , {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({phone_number: number, password: password_input})
            
            }).then(response => {
                return response;
            });

        } catch(err) {
            console.log(err);
        };
    },

    // Create (or register) a new user account
    create_user(newUser) {

        return fetch(`${baseUrl}/users/create_account` , {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({user: newUser})
        
        }).then(response => {
            if(!(response.status === 201)) {
                return { failed: true };
            }
            return response.json();
        }).then(jsonResponse => {
            if(jsonResponse.failed){
                return jsonResponse;
            }
            if(jsonResponse.token?.accessToken){
                localStorage.setItem("user", JSON.stringify(jsonResponse.token));
                storeEntitlements(jsonResponse.entitlements);
                setAnalyticsUserId(jsonResponse.token.userId);
                return jsonResponse;
            }
            return { failed: true };
        }).catch(() => ({ failed: true }));
        
    },

    uniqueEmail(email) {
        return uniqueLookup(`${baseUrl}/users/checkUnique1/${email}`);
    },

    uniquePhone(phone) {
        return uniqueLookup(`${baseUrl}/users/checkUnique2/${phone}`);
    },

    uniqueUser(username) {
        return uniqueLookup(`${baseUrl}/users/checkUnique3/${username}`);
    },

    // get the user's account details
    account_details(){
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/users/account_details`, {
            headers: headers
        })
        .then(response => {
            return response.json();

        }).then (jsonResponse => {
            if (jsonResponse) {
                return jsonResponse;
            } else { 
                return null;
            };
        });
    },

    // get - resend email verification
    resend_email_verification(){
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/users/verify/email/resend`, {
            headers: headers
        })
        .then(response => {
            return response.json();

        }).then (jsonResponse => {
            if (jsonResponse) {
                return jsonResponse;
            } else { 
                return null;
            };
        });
    },

    /* -----------------  settings --------------------------------- */

    // post - create a secondary email
    add_secondary_email(secondary_email) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/users/verify/email/create_secondary`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                secondary_email: secondary_email
            })
    
        }).then(response => {
            return response.json();
        });
    },


    //get a list of all verified secondary emails
    get_secondary_emails() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/users/secondary_emails`, {
            headers: headers
        })
        .then(response => {
            return response.json();

        }).then (jsonResponse => {
            if (jsonResponse.email_list) {
                return jsonResponse.email_list;
            } else { 
                return [];
            };
        });
        
    },

    // post an email to get a reset password email
    reset_password_email(email) {
        try {
            return fetch(`${baseUrl}/users/password/reset_email` , {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({email: email})
            
            }).then(response => {
                return response.json();
            }).then(jsonResponse => {
                return jsonResponse;
            });
        } catch(e) {
            console.log(e);
        };
    },


    checkPwdResetToken(token) {
        return fetch(`${baseUrl}/users/password/check_token`, {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({token: token})
        })
        .then(response => {
            return response.json();
        }).then (jsonResponse => {
            return jsonResponse.tokenIsValid;
        })
        
    },

    create_new_password(token, new_password){
        return fetch(`${baseUrl}/users/password/reset_password_from_email`, {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({token: token, password: new_password})
        })
        .then(response => {
            return response.json();
        })
    },

    create_new_password_from_login(new_password){
        return fetch(`${baseUrl}/users/password/reset_password_from_login`, {
            method: 'POST',
            headers: 
                {   
                    "Content-Type": "application/json",
                    'x-access-token': authServices.authHeaders()['x-access-token']  
                },
            body: JSON.stringify({password: new_password})
        })
        .then(response => {
            return response.json();
        })
    },

    //get a list of the notifications status
    getNotificationSettings() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/users/notifications`, {
            headers: headers
        })
        .then(response => {
            return response.json();

        }).then (jsonResponse => {
            console.log(jsonResponse)
            if (jsonResponse.notifications) {
                return jsonResponse.notifications[0];
            } else { 
                return {
                    redeemGiftReminders: false,
                    birthdayOccasionReminders: false,
                    productGuides: false,
                    ageBasedTips: false,
                    personalizedContent: false,
                    partnerOffers: false,
                    announcements: false,
                };
            };
        });
        
    },

    updateNotificationSetting(setting, status){
            const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        console.log('updating notification')
        return fetch(`${baseUrl}/users/notifications`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                setting: setting,
                status: status
            })

        }).then(response => {
            return response.json();
        });
    },


    /* -----------------  send gifts and gifting history  --------------------------------- */

    // post a new gift / give a new gift
    send_gift(gift, media, pi_id, pm, ref) {
        gift.send_to_email = gift.send_to_email.toLowerCase();
        const user = JSON.parse(localStorage.getItem('user'));
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/sent_gifts/` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   gift: gift, 
                    from: gift.gift_from ? gift.gift_from : user.first_name + ' ' + user.last_name, 
                    mediaUrl: media,
                    pi_id: pi_id,
                    pm_id: pm.id,
                    pm_type: pm.type,
                    ref: ref ? ref : null
                })
        
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            return jsonResponse;
        });
    },

    // post a new gift / give a new gift AS A GUEST
    send_gift_as_guest(gift, media, pi_id, pm_id, ref, opt_in) {
        gift.send_to_email = gift.send_to_email.toLowerCase();
        const headers = 
            { 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/sent_gifts/guest` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   gift: gift,  
                    mediaUrl: media,
                    pi_id: pi_id,
                    pm_id: pm_id,
                    ref: ref ? ref : null,
                    opt_in: opt_in
                })
    
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            return jsonResponse;
        });
    },

    gift_error(response, sender_email, failed_count){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/error/registered` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   stripe_response: response,
                    sender_email: sender_email ? sender_email : null,
                    failed_count: failed_count
                })
        })
    },

    gift_error_guest(response, sender_email, failed_count){
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/error/guest` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   stripe_response: response,
                    sender_email: sender_email ? sender_email : null,
                    failed_count: failed_count
                })
        })
    },

    //get a list of all the gifts that a user sent
    sent_gifts() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/sent_gifts/`, {
            headers: headers
        })
        .then(response => {
            return response.json();

        }).then (jsonResponse => {
            if (jsonResponse.sentGifts) {
                return jsonResponse.sentGifts;
            } else { 
                return [];
            };
        });
        
    },

    // get one sent gift by id (for the detail page)
    get_sent_gift(gift_id) {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/sent_gifts/${gift_id}`, {
            headers: headers
        })
        .then(response => response.json())
        .then(jsonResponse => {
            return jsonResponse; // { gift, plan, gift_number }
        });
    },

    /* -----------------  media uploads  --------------------------------- */

    // upload an image / media to the aws s3 bucket
    async upload_media(file, destination) {
        if(!file) {
            return {mediaUrl: ''};
        } else if (file && typeof file === 'string'){
            return {mediaUrl: file};
        } else {
            const fd = new FormData();
            fd.append('destination', destination);
            fd.append('media', file, file.name);
            
            return fetch(`${baseUrl}/sent_gifts/media_upload` , {
                method: 'POST',
                
                body: fd
            
            }).then(response => {
                return response.json();
            
            }).then(jsonResponse => {
                return jsonResponse;
            }).catch(err => {
                alert('There was an error when trying to upload the image. Action will continue without the attached media.');
                return {mediaUrl: ''};
            });
        } 
    },

    /* -----------------  interact with received gifts  --------------------------------- */

    open_new_gift(gift_id) {
        return fetch(`${baseUrl}/received_gifts/open_new_gift/${gift_id}`)
        .then(response => {
            return response.json();
        }).then(jsonResponse => {
            if (jsonResponse.requestedGift) {
                return {requestedGift: jsonResponse.requestedGift, from: jsonResponse.from};
            } else {
                return {requestedGift: {}, from: '', access: 'No-access'};
            }
        });
    },

    // get all of a user's received gifts
    received_gifts() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/received_gifts/all_received_gifts/`, {
            headers: headers
        })
        .then(response => {
            return response.json();

        }).then (jsonResponse => {
            return jsonResponse;
        });
        
    },

    // get the details of a recevied gift
    received_gift(gift_id) {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/received_gifts/received_gift/${gift_id}`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then(jsonResponse => {
            if (jsonResponse.requestedGift) {
                return {requestedGift: jsonResponse.requestedGift, from: jsonResponse.from, redeem_status: jsonResponse.redeem_status};
            } else {
                return {requestedGift: {}, from: '', access: 'No-access'};
            }
        });
    },

    // get the details of a matched gift (for matched gift redemption page)
    received_matched_gift(gift_id) {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/received_gifts/received_matched_gift/${gift_id}`, { 
            headers })
        .then(response => response.json())
        .then(jsonResponse => {
            if (jsonResponse.requestedGift) return jsonResponse;
            return { requestedGift: {}, matcherName: null, matcherRelationship: null, redeem_status: null, access: 'No-access' };
        });
    },

    // get details of redeemed gift
    redeemed_gift(gift_id) {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/received_gifts/redeemed_gift/${gift_id}`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then(jsonResponse => {
            if (jsonResponse.applied_on) {
                return jsonResponse;
            } else {
                return {};
            }
        });
    },

    // get the names and id of the recipient accounts
    get_recipient_accounts() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/received_gifts/recipients/`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        });
    },

    // post redemption details, based on gift id, child account id, and fund id
    redeem_gift(redemption_details) {
        /* redemption_details schema = {
                gift_id,
                child_account_id, 
                fund_id
            } */
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/received_gifts/redeem_gift` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({redemption_details: redemption_details})
        
        }).then(response => {
            return response.json();
        
        })
    },

    // post thank you note
    send_thank_you(thank_you_note, thank_you_media, gift_id){
        let thank_you = {
            thank_you_note: thank_you_note,
            thank_you_media: thank_you_media
        }
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/received_gifts/send_thank_you` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   thank_you: thank_you,
                    gift_id: gift_id
                })
        
        }).then(response => {
            return response.json();
        })
    },

    /* -----------------  interact with gg plans and recipients  --------------------------------- */

    // add a new recipient account / plan
    add_account(accountInfo) {
        const headers = 
        {
        'x-access-token': authServices.authHeaders()['x-access-token'], 
        "Content-Type": "application/json"
        };

        return fetch(`${baseUrl}/plans/add_account/` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({accountInfo: accountInfo})
        
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            console.log(jsonResponse);
            return jsonResponse;
        });
        
    },

    // add a new recipient account / plan with gifting page
    add_account_w_page(accountInfo, media) {
        const headers = 
        {
        'x-access-token': authServices.authHeaders()['x-access-token'], 
        "Content-Type": "application/json"
        };

        return fetch(`${baseUrl}/plans/add_account_w_page` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({accountInfo: accountInfo, media: media})
        
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            console.log(jsonResponse);
            return jsonResponse;
        });
        
    },

    // get all of the plans associated with a user id
    get_plans() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/plans/list/`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then (jsonResponse => {
            if (jsonResponse.plans) {
                return jsonResponse.plans;
            } else { 
                return [];
            };
        })
    },

    // get plan information by the plan id number
    get_plan_by_id(planId) {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/plans/plan/${planId}`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then(jsonResponse => {
            console.log(jsonResponse);
            if (jsonResponse.fetched_plan) {
                return {fetched_plan: jsonResponse.fetched_plan, other_registries: jsonResponse.other_registries};
            } else {
                return {access: 'No-access'};
            }
        });
    },

    unique_child_account(child_name){
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/plans/add_account/check_unique/${child_name}`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then (jsonResponse => {
            return jsonResponse.unique; 
        })
    },


    // add a new recipient account / plan
    update_recipient_account(account_name, update_name, update_birthday) {
        const headers = 
        {
        'x-access-token': authServices.authHeaders()['x-access-token'], 
        "Content-Type": "application/json"
        };

        return fetch(`${baseUrl}/plans/update_account` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({account_name: account_name, update_name: update_name, update_birthday: update_birthday})
        
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            console.log(jsonResponse);
            return jsonResponse;
        });
        
    },


    // post request for gifts
    share_request_for_gifts(captcha, recipient_name, asker_name, child, asker_email, recipient_email, media) {
        asker_email = asker_email.toLowerCase();
        recipient_email = recipient_email.toLowerCase();

        const headers = 
            { 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/plans/share_request_for_gifts/` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   captcha: captcha,
                    recipient_name: recipient_name, 
                    asker_name: asker_name, 
                    child: child, 
                    asker_email: asker_email, 
                    recipient_email: recipient_email, 
                    media: media
                })
    
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            return jsonResponse;
        });
    },

    /* -----------------  gifting pages  --------------------------------- */

    // get plan information by the plan id number
    get_gifting_page(slug) {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/plans/gifting_page/${slug}`, {
            headers: headers
        })

        .then(response => {
            
            return response.json();

        }).then(jsonResponse => {
            console.log(jsonResponse);
            if (jsonResponse.gifting_page) {
                return {
                    gifting_page: jsonResponse.gifting_page,
                    sent_gifts: jsonResponse.sent_gifts,
                    gifting_link: jsonResponse.gifting_link,
                    gifts_activity: jsonResponse.gifts_activity,
                    other_registries: jsonResponse.other_registries,
                    matching_available: !!jsonResponse.matching_available,
                    matching_remaining_cents: jsonResponse.matching_remaining_cents || 0,
                    matching_multiplier: jsonResponse.matching_multiplier || 2,
                    monthly_eligible: !!jsonResponse.monthly_eligible
                };
            } else {
                return {access: 'No-access'};
            }
        });
    },

    /** Public. Get matching info for give-a-gift flow when opened via link. Prefer ref + account_id; fallback ref + gift_for. */
    get_matching_for_gift(ref, account_id_or_gift_for, use_account_id = false) {
        const params = new URLSearchParams();
        params.set('ref', String(ref));
        if (use_account_id) {
            params.set('account_id', String(account_id_or_gift_for));
        } else {
            params.set('gift_for', String(account_id_or_gift_for || ''));
        }
        return fetch(`${baseUrl}/plans/matching_for_gift?${params}`)
            .then((r) => r.json())
            .then((data) => ({
                matching_available: !!data.matching_available,
                matching_remaining_cents: data.matching_remaining_cents || 0,
                matching_multiplier: data.matching_multiplier || 2,
                monthly_eligible: !!data.monthly_eligible,
                registry_context: data.registry_context || null,
            }));
    },

    // post - update the gifting page
    update_gifting_page(child_account_id, page_active, welcome_message, media_url, goals, show_activity, display_other_registries, other_registries, show_monthly_gifting_option, is_public){
        const headers =
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/plans/update_gifting_page` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                child_account: child_account_id,
                page_active: page_active,
                welcome_message: welcome_message,
                media_url: media_url,
                goals: goals,
                show_activity: show_activity,
                display_other_registries: display_other_registries,
                other_registries: other_registries,
                // Only sent when caller is GG+; server silently ignores this field for non-premium callers.
                show_monthly_gifting_option: show_monthly_gifting_option,
                is_public: is_public
            })
        
        }).then(response => {
            return response.json();

        })
    },

    /* -----------------  gift matching (per plan)  --------------------------------- */

    // GET list of matching programs for a plan (with aggregates)
    get_matching(planId) {
        const headers = authServices.authHeaders();
        return fetch(`${baseUrl}/matching/plan/${planId}`, { headers })
            .then(response => response.json())
            .then(json => {
                if (json.matching_programs !== undefined) return json.matching_programs;
                return [];
            });
    },

    // POST create a matching program for a plan
    create_matching(planId, body) {
        const headers = {
            ...authServices.authHeaders(),
            'Content-Type': 'application/json'
        };
        return fetch(`${baseUrl}/matching/plan/${planId}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        }).then(async (response) => {
            const json = await response.json();
            return { ...json, ok: response.ok, status: response.status };
        });
    },

    // POST update a matching program
    update_matching(planId, programId, body) {
        const headers = {
            ...authServices.authHeaders(),
            'Content-Type': 'application/json'
        };
        return fetch(`${baseUrl}/matching/plan/${planId}/matching/${programId}/update`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        }).then(response => response.json());
    },

    /* -----------------  monthly gifts  --------------------------------- */

    // Send a transactional email invite for a monthly gift on a given plan.
    send_monthly_gift_invite(planId, recipient_name, recipient_email) {
        const headers = {
            ...authServices.authHeaders(),
            'Content-Type': 'application/json'
        };
        return fetch(`${baseUrl}/monthly_gifts/plan/${planId}/invite`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ recipient_name, recipient_email })
        }).then((r) => r.json());
    },

    // Start monthly gift subscription (setup + first invoice)
    monthly_gift_setup(payload) {
        const headers = {
            'Content-Type': 'application/json',
            ...authServices.authHeaders()
        };
        return fetch(`${baseUrl}/monthly_gifts/setup`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        }).then((r) => r.json());
    },

    // after payment — activate plan and create first gift
    monthly_gift_finalize(payload) {
        const headers = {
            'Content-Type': 'application/json',
            ...authServices.authHeaders()
        };
        return fetch(`${baseUrl}/monthly_gifts/finalize`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        }).then((r) => r.json());
    },

    /** Public. Get matcher invite details by token (for confirm page). No auth. */
    get_matcher_invite_details(token) {
        return fetch(`${baseUrl}/matching/invite/details?t=${encodeURIComponent(token)}`)
            .then((r) => r.json());
    },

    /** Public. Confirm matcher invite by token. No auth. */
    confirm_matcher_invite(token) {
        return fetch(`${baseUrl}/matching/invite/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token })
        }).then((r) => r.json());
    },

    /** Public. Get monthly matching details by token. No auth. */
    get_monthly_matching_details(token) {
        return fetch(`${baseUrl}/matching/monthly/details?t=${encodeURIComponent(token)}`)
            .then((r) => r.json());
    },

    /** Confirm monthly matching CC payment after confirmCardPayment succeeds. Creates matched gifts synchronously. */
    confirm_monthly_matching_payment(token, payment_intent_id) {
        return fetch(`${baseUrl}/matching/monthly/confirm_payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, payment_intent_id })
        }).then((r) => r.json());
    },

    /** Create payment intent for monthly matching. Optional auth for saved PM. */
    create_monthly_matching_payment(token, payment_method_id, pm_type) {
        const headers = {
            'Content-Type': 'application/json',
            ...authServices.authHeaders()
        };
        const body = { token, pm_type: pm_type || 'CC' };
        if (payment_method_id) body.payment_method_id = payment_method_id;
        return fetch(`${baseUrl}/matching/monthly/create_payment`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        }).then((r) => r.json());
    },

    /** Create ACH charge for monthly matching (Charges API, same style as gifting flow). Requires auth. */
    create_monthly_matching_ach_charge(token, payment_method_id) {
        const headers = {
            'Content-Type': 'application/json',
            ...authServices.authHeaders()
        };
        return fetch(`${baseUrl}/matching/monthly/create_ach_charge`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ token, payment_method_id })
        }).then((r) => r.json());
    },

    /* -----------------  monthly gift manage / cancel (M11)  ----------------------- */

    /** Public. Get monthly gift plan summary by manage token. No auth. */
    get_monthly_gift_manage(token) {
        return fetch(`${baseUrl}/monthly_gifts/manage?t=${encodeURIComponent(token)}`)
            .then((r) => r.json());
    },

    /** Public. Cancel a monthly gift plan by manage token. No auth. */
    cancel_monthly_gift(token) {
        return fetch(`${baseUrl}/monthly_gifts/cancel?t=${encodeURIComponent(token)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        }).then((r) => r.json());
    },

    /* -----------------  linked accounts & payments  --------------------------------- */
    
    get_recipient_funds(recipient_id){
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/received_gifts/get_recipient_funds/${recipient_id}`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then(jsonResponse => {
            if (jsonResponse.linked_accounts) {
                // response may have a list, that contains items
                // each item has: fund_type, institution_name, fund_api_id, fund_last_4, fund_logo
                let linked_accounts = jsonResponse.linked_accounts;
                // possible fund types: 529 Plan, Investment Account, Savings Account, Checking Account, US Savings Bonds
                
                //create an array with all the categories
                let categories = [];
                let list = [];
                
                
                let checking_redeem_fee    =  `${String((gg_fees.redeem_fees.checking_variable   *100).toFixed(1))}% ${gg_fees.redeem_fees.checking_fixed    === 0 ? '': '+ '+gg_fees.redeem_fees.checking_fixed+'¢' }`;
                let other_redeem_fee       =  `${String((gg_fees.redeem_fees.other_variable      *100).toFixed(1))}% ${gg_fees.redeem_fees.other_fixed       === 0 ? '': '+ '+gg_fees.redeem_fees.other_fixed+'¢' }`;

                linked_accounts.forEach(element => { 
                    if(element.fund_type === '529 Plan') {
                        if(!categories.includes('529 Plan')){
                            list.push({
                                id: 1,
                                category_header: '529 Plans',
                                // category_subtext: other_redeem_fee + ' To Redeem',
                                category_subtext: gg_fees.redeem_fees.other_fixed === 0 && gg_fees.redeem_fees.other_variable === 0 ? 'No Fees' : other_redeem_fee + ' To Redeem',
                                items: []
                            });
                            categories.push(element.fund_type);
                        }
                    } else if(element.fund_type === 'Investment Account') {
                        if(!categories.includes('Investment Account')){
                            list.push({
                                id: 2,
                                category_header: 'Investment Accounts',
                                // category_subtext: other_redeem_fee + ' To Redeem',
                                category_subtext: gg_fees.redeem_fees.other_fixed === 0 && gg_fees.redeem_fees.other_variable === 0 ? 'No Fees' : other_redeem_fee + ' To Redeem',
                                items: []
                            });
                            categories.push(element.fund_type);
                        }
                    /*
                    } else if(element.fund_type === 'US Savings Bonds') {
                        if(!categories.includes('US Savings Bonds')){
                            list.push({
                                id: 3,
                                category_header: 'US Savings Bonds',
                                // category_subtext: other_redeem_fee + ' To Redeem',
                                category_subtext: gg_fees.redeem_fees.other_fixed === 0 && gg_fees.redeem_fees.other_variable === 0 ? 'No Fees' : other_redeem_fee + ' To Redeem',
                                items: []
                            });
                            categories.push(element.fund_type);
                        }
                        */
                    } else if(element.fund_type === 'Savings Account') {
                        if(!categories.includes('Savings Account')){
                            list.push({
                                //id: 4,
                                id: 3,
                                category_header: 'Savings Accounts',
                                // category_subtext: other_redeem_fee + ' To Redeem',
                                category_subtext: gg_fees.redeem_fees.other_fixed === 0 && gg_fees.redeem_fees.other_variable === 0 ? 'No Fees' : other_redeem_fee + ' To Redeem',
                                items: []
                            });
                            categories.push(element.fund_type);
                        }
                    } else if(element.fund_type === 'Checking Account') {
                        if(!categories.includes('Checking Account')){
                            list.push({
                                //id: 5,
                                id: 4,
                                category_header: 'Checking Accounts',
                                category_subtext: checking_redeem_fee + ' To Redeem',
                                color: 'gg_red',
                                items: []
                            });
                            categories.push(element.fund_type);
                        }
                    };

                    // add the item to the item list in the relevant category
                    function checkCategoryExists(listItem) {
                        //return (listItem.category_header === element.fund_type + (listItem.category_header === 'US Savings Bonds' ? '' : 's'));
                        return (listItem.category_header === element.fund_type + 's');
                    }
                    let index = list.findIndex(checkCategoryExists);
                    let newItem = {   
                        image: element.fund_logo,
                        main_text: element.institution_name,
                        sub_text: '•••• '+element.fund_last_4,
                        id: element.fund_api_id,
                        fund_type: element.fund_type
                        };

                    list[index].items.push(newItem);
                });
                // sort the list by the category id number
                let sorted_list = list.sort((a,b) => a.id - b.id);
                return sorted_list;
            } else {
                let emptyList = [];
                return emptyList;
            }  
        })
    },

    /* -----------------  Stripe Connect Account integrations  --------------------------------- */
    
    // create a stripe account and associate it with the GG user
    create_stripe_account() {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/create_stripe_account`, {
            method: 'POST',
            headers: headers
    
        }).then(response => {
            console.log('received response');
            console.log(response);
        });
    },
    
    // update stripe account info with verification details
    update_stripe_account(digits, dob) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/update_personal_stripe`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                digits: digits, 
                dob: dob
            })
    
        }).then(response => {
            console.log('received response');
            console.log(response);
        });
    },

    // get request - check if user already linked any bank accounts or not
    check_first_link() {
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/stripe/check_first_linked`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then (jsonResponse => {
            if (jsonResponse.first_link) {
                return jsonResponse.first_link;
            } else { 
                return false;
            };
        })
    },

    // send a stripe token to the GG server, and have it associated with a user
    create_bank(stripe_token, recipient, account_type, institution_name, logo, public_token, plaid_account_id, from_plaid) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/create_bank`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                token: stripe_token, 
                child_account: recipient, 
                account_type: account_type,
                institution_name: institution_name,
                logo: logo,
                from_plaid: from_plaid,
                public_token: public_token, 
                plaid_account_id: plaid_account_id,
            })
    
        }).then(response => {
            console.log('received response');
            console.log(response);
            return response.json();
        
        }).then(jsonResponse => {
            console.log(jsonResponse);
            return jsonResponse;
        });
    },

    /* -----------------  Stripe Customer integrations  --------------------------------- */

    // create a stripe account and associate it with the GG user
    create_stripe_customer() {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/create_customer`, {
            method: 'POST',
            headers: headers
    
        }).then(response => {
            console.log('received response');
            console.log(response);
        });
    },

    // send a pm to the gg  db and save it to a customer
    pm_create(pm, pm_type, pm_name, pm_last4, logo) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_method/cc`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                pm: pm, 
                pm_type: pm_type,
                pm_name: pm_name,
                pm_last4: pm_last4,
                logo: logo
                })
    
        }).then(response => {
            return response.json();
        
        });
    },

    // get list of payment methods
    pm_get(isMonthlyGift = false){
        let headers = authServices.authHeaders();
        return fetch(`${baseUrl}/stripe/payment_method`, {
            headers: headers
        })
        .then(response => {
            return response.json();
        }).then(jsonResponse => {
            if (jsonResponse.payment_methods) {
                // response may have a list, that contains items
                // each item has: method_type, method_api_id, method_logo, method_name, method_last4
                let payment_methods = jsonResponse.payment_methods;
                // possible payment method types: card, bank

                //create an array with all the categories
                let categories = [];
                
                let list = [];

                // ACH banks use method_type ACH; pending vs verified is the verified flag (0 vs 1).
                const isAchBank = (pm) => pm.method_type === 'ACH';
                const isAchVerified = (pm) => isAchBank(pm) && (pm.verified === 1 || pm.verified === true);
                const isAchPending = (pm) => isAchBank(pm) && !isAchVerified(pm);

                payment_methods.forEach(element => { 
                    if(element.method_type === 'CC') {
                        if(!categories.includes('CC')){
                            list.push({
                                id: 2,
                                category_header: 'Credit Cards',
                                category_subtext: getSendFeeCategorySubtext('CC', isMonthlyGift),
                                items: [],
                                color: 'gg_red'
                            });
                            categories.push(element.method_type);
                        }
                    } else if(isAchVerified(element)) {
                        if(!categories.includes('ACH')){
                            list.push({
                                id: 1,
                                category_header: 'Bank Debits - ACH',
                                category_subtext: getSendFeeCategorySubtext('ACH', isMonthlyGift),
                                items: []
                            });
                            categories.push('ACH');
                        }

                    } else if(isAchPending(element)) {
                        if(!categories.includes('ACH_pending')){
                            list.push({
                                id: 3,
                                category_header: 'Bank Debits - ACH (pending verification)',
                                category_subtext: "Verification Required",
                                items: [],
                                disabled: true
                            });
                            categories.push('ACH_pending');
                        }
                    };
                

                    // add the item to the item list in the relevant category
                    function checkCategoryExists(listItem) {
                        if(listItem.category_header === 'Credit Cards' && element.method_type === 'CC') {
                            return true;
                        } else if (listItem.category_header === 'Bank Debits - ACH' && isAchVerified(element)){
                            return true;
                        } else if (listItem.category_header === 'Bank Debits - ACH (pending verification)' && isAchPending(element)){
                            return true;
                        } else {
                            return false;
                        }
                    }
                    let index = list.findIndex(checkCategoryExists);
                    if (index < 0) {
                        console.error('pm_get: unmapped payment method type', element.method_type, element.verified);
                        return;
                    }
                    let newItem = {   
                        id: element.method_api_id,
                        type: isAchBank(element) ? 'ACH' : element.method_type,
                        verified: isAchBank(element) ? isAchVerified(element) : undefined,
                        main_text: element.method_name,
                        sub_text: '•••• '+element.method_last4,
                        image: element.method_logo,
                        integration: element.integration
                        };

                    list[index].items.push(newItem);
                });
                // sort the list by the category id number
                let sorted_list = list.sort((a,b) => a.id - b.id);
                return sorted_list;
            } else {
                let emptyList = [];
                return emptyList;
            }  
        })
    },

    bank_create(tokenId, logo, from_plaid, public_token, plaid_account_id){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_method/bank`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                token: tokenId,
                logo: logo,
                
                from_plaid: from_plaid,

                public_token: public_token, 
                plaid_account_id: plaid_account_id
                })
    
        }).then(response => {
            return response.json();
        
        });
    },

    // Step 1 of the FE-to-Stripe manual bank flow: create an unconfirmed SetupIntent on the BE.
    // Returns { client_secret, setup_intent_id, holder_name, email } 
    bank_manual_start(){
        const headers = {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            'Content-Type': 'application/json',
        };
        return fetch(`${baseUrl}/stripe/payment_method/bank_manual/start`, {
            method: 'POST',
            headers: headers,
        }).then(response => response.json());
    },

    // Step 3 of the FE-to-Stripe manual bank flow: after stripe.confirmUsBankAccountSetup() succeeds,
    // tell the BE to persist the pm_ + mandate from the SetupIntent to the GG database.
    bank_manual_complete(setup_intent_id){
        const headers = {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            'Content-Type': 'application/json',
        };
        return fetch(`${baseUrl}/stripe/payment_method/bank_manual/complete`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ setup_intent_id }),
        }).then(response => response.json());
    },

    // Financial Connections instant bank collection
    bank_fc_start(){
        const headers =
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/stripe/payment_method/bank_fc/start`, {
            method: 'POST',
            headers: headers,
        }).then(response => {
            return response.json();
        });
    },

    // complete the financial connections bank collection
    bank_fc_complete(setup_intent_id){
        const headers =
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/stripe/payment_method/bank_fc/complete`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                setup_intent_id: setup_intent_id,
            })
        }).then(response => {
            return response.json();
        });
    },

    // Fetch SetupIntent client_secret when Stripe expects client-side microdeposit verify.
    bank_verify_prepare(bank_id){
        const headers =
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/stripe/payment_method/bank_verify/prepare`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ bank_id: bank_id })
        }).then(response => {
            return response.json();
        });
    },

    // Verify a manually-added bank with the 6-character descriptor code from the statement deposit.
    verify_manual_bank(descriptor_code, bank_id){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_method/verify_bank_manual`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                descriptor_code: descriptor_code,
                bank_id: bank_id
                })

        }).then(response => {
            return response.json();
        });
    },

    // Remove a legacy unverified bank when the user clicks Verify (outdated ba_ flow).
    remove_outdated_bank(bank_id){
        const headers =
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/stripe/payment_method/remove_outdated_bank`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ bank_id: bank_id })
        }).then(response => {
            return response.json();
        });
    },

    // Remove a legacy ACH bank (verified ba_) so the user can re-add on the new model.
    remove_legacy_bank(bank_id){
        const headers =
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };

        return fetch(`${baseUrl}/stripe/payment_method/remove_legacy_bank`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ bank_id: bank_id })
        }).then(response => {
            return response.json();
        });
    },

    /* -----------------  Plaid integrations  --------------------------------- */

    get_plaid_link(link_type){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/plaid/create_link_token`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({link_type: link_type}) 

        }).then(response => {
                return response.json();
        });
    },

    track_fund_balance(public_token, fund_id){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/plaid/track_fund_balance`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({public_token: public_token, fund_id: fund_id}) 

        }).then(response => {
                return response.json();
        });
    },

    /* -----------------  Stripe charges integrations  --------------------------------- */

    // create a stripe charge for a gift with ACH
    create_ach_charge(pi_id, payment_method_id, centAmount, accepted_fees, receipt_email, chosen_fee_cents) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/charges/ach`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                pi_id: pi_id,
                payment_method_id: payment_method_id,
                amount: centAmount,
                accepted_fees: accepted_fees,
                receipt_email: receipt_email,
                chosen_fee_cents
            })
    
        }).then(response => {
                return response.json();
                /* if limits are failed:
                    {   
                        limit_passed: true, 
                        limit_fail: 'weekly_limit' / 'transaction_limit'
                        next_available: next_available,
                        transaction_limit: limit in $ amount,
                        remaining_weekly: (weekly_limit - weekly_volume)/100
                    }
                */
            
        });
    },

    /* -----------------  Stripe PI integrations  --------------------------------- */


    // create a payment_intent - separate charge & transfer
    pi_create_separate_registered(centAmount) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/create_separate/registered`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({amount: centAmount})
    
        }).then(response => {
            return response.json();
        });
    },

    // update a payment_intent - new amount
    pi_update_amount(payment_intent, total_amount, orig_gift_amount) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/update_amount`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({pi_id: payment_intent, amount: total_amount, orig_gift_amount: orig_gift_amount})
    
        }).then(response => {
            return response.json();
        });
        
    },

    // finalize a payment_intent - before the confirmation
    pi_finalize(payment_intent, payment_method, recipient, centAmount, chosen_fee) {
        let new_recipient = recipient ? recipient : null;
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/finalize`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                pi_id: payment_intent, 
                payment_method_id: payment_method.id,
                recipient: new_recipient,
                amount: centAmount, 
                chosen_fee: chosen_fee
            })
    
        }).then(response => {
                return response.json();
                /* if limits are failed:
                    {   
                        limit_passed: true, 
                        limit_fail: 'weekly_limit' / 'transaction_limit' / 'under_minimum'
                        next_available: next_available,
                        transaction_limit: limit in $ amount,
                        remaining_weekly: (weekly_limit - weekly_volume)/100
                    }
                */
            
        });
    },

    /* -----------------  Stripe PI integrations (GUEST)  --------------------------------- */


    // create a payment_intent - separate charge & transfer
    pi_create_separate_guest(centAmount) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/create_separate/guest`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({amount: centAmount})
    
        }).then(response => {
            return response.json();
        });
    },

    // finalize a payment_intent for guests - before the confirmation
    pi_finalize_guest(payment_intent, payment_method, recipient, centAmount, sender_email, captcha, chosen_fee) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/finalize_guest`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                pi_id: payment_intent, 
                payment_method_id: payment_method.id,
                recipient: recipient,
                amount: centAmount,
                guest: true,
                guest_sender_email: sender_email,
                captcha: captcha,
                chosen_fee: chosen_fee
            })
    
        }).then(response => {
            return response.json();
            /* if limits are failed:
                {   
                    limit_passed: true, 
                    limit_fail: 'weekly_limit' / 'transaction_limit'
                    next_available: next_available
                }
            */
           // if response has a captcha item - true / false
        });
    },

    /* -----------------  Stripe PI - Destination  --------------------------------- */


    // check if the send to details are for a registered user
    get_receiver_stripe_acc(channel, channel_input){
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/get_receiver_stripe_acc` , {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(
                {   channel: channel, 
                    channel_input: channel_input
                }
            )
        
        }).then(response => {
            return response.json();
        
        }).then(jsonResponse => {
            return jsonResponse;
            
        });
    },

    // create a payment_intent - destination charge
    pi_create_destination_registered(centAmount, recipient) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/create_destination/registered`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({amount: centAmount, recipient: recipient})
    
        }).then(response => {
            return response.json();
        });
    },

    // create a payment_intent - destination charge
    pi_create_destination_guest(centAmount, recipient) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/create_destination/guest`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({amount: centAmount, recipient: recipient})
    
        }).then(response => {
            return response.json();
        });
    },

    // update a payment_intent - new recipient info
    pi_update_destination_registered(payment_intent, recipient) {
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'], 
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/update_destination/registered`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({pi_id: payment_intent, recipient: recipient})
    
        }).then(response => {
            return response.json();
        });
    },

    // update a payment_intent - new recipient info
    pi_update_destination_guest(payment_intent, recipient) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/payment_intent/update_destination/guest`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({pi_id: payment_intent, recipient: recipient})
    
        }).then(response => {
            return response.json();
        });
    },


    /* -----------------  Stripe Subscriptions  --------------------------------- */

    create_checkout_session(price, { source = null, return_to = null } = {}){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/subscriptions/create_checkout`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                amount: Number(price),
                source: source || undefined,
                return_to: return_to || undefined,
            })
    
        }).then(response => {
            return response.json();
        });
    },

    trial_eligible() {
        const headers = authServices.authHeaders();
        return fetch(`${baseUrl}/stripe/subscriptions/trial_eligible`, { headers })
            .then(response => response.json());
    },


    create_portal_session(flow){
        const headers = 
            {
            'x-access-token': authServices.authHeaders()['x-access-token'],
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/stripe/subscriptions/portal_session`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ flow })
    
        }).then(response => {
            return response.json();
        });
    },

    /** Clear cancel_at_period_end on the user's GG+ subscription (undo scheduled cancel). */
    reactivate_membership() {
        const headers = authServices.authHeaders();
        return fetch(`${baseUrl}/stripe/subscriptions/reactivate`, {
            method: 'POST',
            headers,
        }).then(response => response.json());
    },


    /* -----------------  admin --------------------------------- */

    // send an admin email notification
    notify_admin(message, data) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/users/admin/notification`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({message: message, data: data})

        }).then(response => {
            return response.json();
        });
    }, 

    // send an admin email notification
    admin_log_event(message) {
        const headers = 
            {
            "Content-Type": "application/json"
            };
        
        return fetch(`${baseUrl}/users/admin/log`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({message: message})

        }).then(response => {
            return response.json();
        });
    }

}

export default GGApi;